import { mapValues } from 'lodash-es';
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { mountRootParcel } from 'single-spa';

import { useCookieBanner, useDarkMode, useLanguage, useParcel } from 'hooks';
import services from 'services';
import { ModuleMeta } from 'types';
import { convertExceptionToJson, deprecatedMethod, getBaseTitle, getConfigs, logger } from 'utils';

import ErrorView from '../ErrorView/ErrorView';
import LoadingProgress from '../LoadingProgress/LoadingProgress';

import Parcel from './Parcel';

export type ApplicationModuleProps = {
  className: string;
  moduleKey: string;
  baseRoute: string;
  oidcModuleId?: string | undefined;
};

const setTitle = (title, faviconUrl) => {
  deprecatedMethod(
    services.appFrame.title.setTitle,
    'setTitle',
    'services.appFrame.title.setTitle',
  )(title, { faviconUrl });
};

const deprecatedMethodMaps = {
  getProfile: 'getProfile({{moduleId}})',
  getToken: 'getAccessToken({{moduleId}})',
  getTokenAsync: 'getAccessTokenAsync({{moduleId}})',
  logout: 'logout()',
};

const ApplicationModule = ({ className, moduleKey, baseRoute, oidcModuleId }: ApplicationModuleProps) => {
  const { t } = useTranslation();
  const { loading, error, parcel, widgetConfig, meta } = useParcel(moduleKey);
  const { darkMode, darkModeAllowed } = useDarkMode();
  const { selectedLanguage } = useLanguage();
  const { acceptedIds } = useCookieBanner();

  useEffect(() => {
    return () => {
      services.appFrame.title.setTitle(getBaseTitle());
    };
  }, []);

  const authServices = services.appFrame.auth;
  const deprecatedOidcMethods = useMemo(() => {
    const oidcProps: Record<keyof typeof deprecatedMethodMaps, Function> = {
      getProfile: () => authServices.getProfile(oidcModuleId) ?? authServices.getProfile(), // doc
      getToken: () => authServices.getAccessToken(oidcModuleId) || authServices.getAccessToken(), // doc
      getTokenAsync: () => authServices.getAccessTokenAsync(oidcModuleId) || authServices.getAccessTokenAsync(), // doc
      logout: () => authServices.logout(), // doc
    };
    if (process.env.NODE_ENV === 'production') {
      return oidcProps;
    }

    return mapValues(oidcProps, (fn, fnName) => {
      const replacementMethod = deprecatedMethodMaps[fnName];
      return deprecatedMethod(
        fn,
        `module(${moduleKey}).props.${String(fnName)}`,
        replacementMethod ? `services.auth.${replacementMethod}` : '???',
      );
    });
  }, [authServices, moduleKey, oidcModuleId]);

  const moduleConfigs = useMemo(() => {
    if (process.env.NODE_ENV === 'production' || !widgetConfig) {
      return widgetConfig;
    }
    const proxy = new Proxy(widgetConfig, {
      get: deprecatedMethod(
        Reflect.get,
        `module(${moduleKey}).props.moduleConfigs`,
        `module(${moduleKey}).props.widgetConfig`,
      ),
    });
    return proxy;
  }, [moduleKey, widgetConfig]);

  const moduleMeta = useMemo(() => {
    if (process.env.NODE_ENV === 'production') {
      return meta;
    }
    const proxy = new Proxy(meta || {}, {
      get: (...args) => {
        if (!meta?.moduleId) {
          logger.warn(`Wanted to reach module meta info, but it is available only for application modules!`);
        }
        return Reflect.get(...args);
      },
    });
    return proxy;
  }, [meta]) as ModuleMeta | null;

  const handleError = useCallback((err: Error) => {
    logger.error('Unexpected internal error in application module:', err);
    services.appFrame.error.main.addError({
      title: 'errors.internal_module_error.title',
      message: 'errors.internal_module_error.message',
      details: {
        type: 'INTERNAL_MODULE_ERROR',
        timestamp: new Date(),
        error: convertExceptionToJson(err),
      },
      actions: [
        {
          id: 'reopen',
          text: 'errors.internal_module_error.actions.reopen',
          onClick: () => {
            services.appFrame.error.main.closeError();
          },
        },
      ],
    });
  }, []);

  const mainNode = services.appFrame.viewport.getMainNode();
  const modalRef = useRef(mainNode);
  modalRef.current = mainNode;

  // TODO:
  // Parcel has extra props which were documented in README.md, but some more powerful
  // automatic documentation should be added by defining a `ApplicationModuleProps` type.
  // Until then every prop is marked with a `// doc` comment.
  return (
    <>
      {loading && <LoadingProgress>{t('progress.loading_module')}</LoadingProgress>}
      {error && <ErrorView {...error} />}
      {parcel && (
        <Parcel
          wrapClassName={className}
          config={parcel}
          mountParcel={mountRootParcel}
          handleError={handleError}
          user={authServices.getUser(oidcModuleId) ?? authServices.getUser()} // doc
          profile={authServices.getProfile(oidcModuleId) ?? authServices.getProfile()} // doc
          token={authServices.getAccessToken(oidcModuleId) ?? authServices.getAccessToken()} // doc
          selectedLanguage={selectedLanguage} // doc
          darkMode={darkModeAllowed ? darkMode : null} // doc
          configs={getConfigs()} // doc
          widgetConfig={widgetConfig} // doc
          moduleConfigs={moduleConfigs} // doc
          moduleMeta={moduleMeta} // doc
          version={process.env.REACT_APP_PACKAGE_VERSION} // doc
          baseRoute={baseRoute} // doc
          setTitle={setTitle} // doc
          modalRef={modalRef} // doc
          services={services} // doc
          cookiePolicy={acceptedIds} // doc
          {...deprecatedOidcMethods}
        />
      )}
    </>
  );
};

export default memo(ApplicationModule);
